import { poDatabase, SUPPLIER_MASTER } from './mockPoStore';
import { grDatabase } from './mockGrStore';
import { MASTER_ITEMS } from './mockPrStore';
import { loadPersistentData, savePersistentData } from './fileStorage';
import { syncSupplierToDatabase } from './dbBridge';

export interface BankAccount {
  bankName: string;
  accountNumber: string;
  accountHolderName: string;
  isPrimary: boolean;
  branch?: string;
}

export interface SupplierRecord {
  id: string;
  code: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  paymentTerms: string; // e.g. 'NET 30', 'NET 14', 'COD', 'CBD'
  status: 'ACTIVE' | 'INACTIVE';
  bankAccounts: BankAccount[];
  createdAt: string;
  updatedAt: string;
}

export interface SupplierProductPriceHistory {
  itemId: string;
  itemCode: string;
  itemName: string;
  category: string;
  uomCode: string;
  lastPurchasePrice: number;
  lastPurchaseDate: string;
  minPrice: number;
  maxPrice: number;
  averagePrice: number;
  totalQuantityPurchased: number;
  totalSpend: number;
  transactionsCount: number;
  priceHistory: Array<{
    poId: string;
    poNumber: string;
    poDate: string;
    orderedQty: number;
    unitPrice: number;
    subtotal: number;
    status: string;
  }>;
}

export interface SupplierWorkspaceData {
  supplier: SupplierRecord;
  kpis: {
    totalSpend: number;
    totalPOsCount: number;
    totalGRsCount: number;
    productsSuppliedCount: number;
    overallAcceptanceRate: number;
  };
  productCatalog: SupplierProductPriceHistory[];
  purchaseOrders: typeof poDatabase;
  goodsReceipts: typeof grDatabase;
}

// Initial Supplier Database
const initialSupplierDatabase: SupplierRecord[] = [
  {
    id: 'sup-001',
    code: 'SUP-001',
    name: 'PT Packaging Jaya Abadi',
    phone: '+62-21-5551234',
    email: 'sales@packagingjaya.co.id',
    address: 'Kawasan Industri MM2100 Blok C, Cikarang Barat, Bekasi',
    paymentTerms: 'NET 30',
    status: 'ACTIVE',
    bankAccounts: [
      {
        bankName: 'Bank Central Asia (BCA)',
        accountNumber: '8830192831',
        accountHolderName: 'PT Packaging Jaya Abadi',
        isPrimary: true,
        branch: 'KCP MM2100 Cikarang',
      },
      {
        bankName: 'Bank Mandiri',
        accountNumber: '1560002938192',
        accountHolderName: 'PT Packaging Jaya Abadi',
        isPrimary: false,
        branch: 'KC Bekasi Juanda',
      },
    ],
    createdAt: '2026-01-10T08:00:00.000Z',
    updatedAt: '2026-09-14T10:00:00.000Z',
  },
  {
    id: 'sup-002',
    code: 'SUP-002',
    name: 'CV Sumber Logistik Mandiri',
    phone: '+62-21-5559876',
    email: 'order@sumberlogistik.co.id',
    address: 'Jl. Rawa Gelam IV No. 12, Kawasan Industri Pulogadung, Jakarta Timur',
    paymentTerms: 'COD',
    status: 'ACTIVE',
    bankAccounts: [
      {
        bankName: 'Bank Mandiri',
        accountNumber: '1240008899123',
        accountHolderName: 'CV Sumber Logistik Mandiri',
        isPrimary: true,
        branch: 'KC Pulogadung',
      },
    ],
    createdAt: '2026-02-15T09:30:00.000Z',
    updatedAt: '2026-09-14T12:00:00.000Z',
  },
  {
    id: 'sup-003',
    code: 'SUP-003',
    name: 'PT Mitra Sukses Industri',
    phone: '+62-21-7788990',
    email: 'info@mitrasukses.com',
    address: 'Jl. Jababeka Raya Kav. 8, Cikarang, Bekasi',
    paymentTerms: 'NET 14',
    status: 'ACTIVE',
    bankAccounts: [
      {
        bankName: 'Bank Danamon',
        accountNumber: '003556789123',
        accountHolderName: 'PT Mitra Sukses Industri',
        isPrimary: true,
        branch: 'KC Cikarang',
      },
    ],
    createdAt: '2026-03-20T10:15:00.000Z',
    updatedAt: '2026-09-14T09:00:00.000Z',
  },
];

export const supplierDatabase: SupplierRecord[] = loadPersistentData('supplier-database.json', initialSupplierDatabase);

let supplierSeq = supplierDatabase.length;

function syncToSupplierMaster(record: SupplierRecord) {
  const existingIdx = SUPPLIER_MASTER.findIndex((s) => s.id === record.id || s.code === record.code);
  const entry = {
    id: record.id,
    code: record.code,
    name: record.name,
    phone: record.phone,
    email: record.email,
    address: record.address,
    paymentTerms: record.paymentTerms,
    bankAccounts: record.bankAccounts.map((b) => ({
      bankName: b.bankName,
      accountNumber: b.accountNumber,
      accountHolderName: b.accountHolderName,
      isPrimary: b.isPrimary,
    })),
  };
  if (existingIdx >= 0) {
    SUPPLIER_MASTER[existingIdx] = entry;
  } else {
    SUPPLIER_MASTER.push(entry);
  }
}

// Sync existing suppliers from supplierDatabase into SUPPLIER_MASTER
for (const s of supplierDatabase) {
  syncToSupplierMaster(s);
}

export function getAllSuppliers(search?: string, paymentTerms?: string): SupplierRecord[] {
  let list = [...supplierDatabase];

  if (search) {
    const q = search.toLowerCase().trim();
    list = list.filter(
      (s) =>
        s.code.toLowerCase().includes(q) ||
        s.name.toLowerCase().includes(q) ||
        s.email.toLowerCase().includes(q) ||
        s.phone.toLowerCase().includes(q) ||
        s.address.toLowerCase().includes(q)
    );
  }

  if (paymentTerms && paymentTerms !== 'ALL') {
    list = list.filter((s) => s.paymentTerms === paymentTerms);
  }

  return list.sort((a, b) => a.code.localeCompare(b.code));
}

export function getSupplierById(id: string): SupplierRecord | undefined {
  const q = id.toLowerCase().trim();
  return supplierDatabase.find((s) => s.id.toLowerCase() === q || s.code.toLowerCase() === q);
}

export function createSupplier(data: {
  code?: string;
  name: string;
  phone: string;
  email: string;
  address: string;
  paymentTerms: string;
  bankAccounts?: BankAccount[];
}): SupplierRecord {
  supplierSeq += 1;
  const code = (data.code || `SUP-${String(supplierSeq).padStart(3, '0')}`).toUpperCase();

  // Validate unique code
  if (supplierDatabase.some((s) => s.code.toUpperCase() === code)) {
    throw new Error(`Supplier code '${code}' already exists.`);
  }

  const nowIso = new Date().toISOString();
  const newSupplier: SupplierRecord = {
    id: `sup-${String(supplierSeq).padStart(3, '0')}`,
    code,
    name: data.name.trim(),
    phone: data.phone.trim(),
    email: data.email.trim(),
    address: data.address.trim(),
    paymentTerms: data.paymentTerms || 'NET 30',
    status: 'ACTIVE',
    bankAccounts: data.bankAccounts && data.bankAccounts.length > 0 ? data.bankAccounts : [
      {
        bankName: 'Bank Central Asia (BCA)',
        accountNumber: '1234567890',
        accountHolderName: data.name.trim(),
        isPrimary: true,
      },
    ],
    createdAt: nowIso,
    updatedAt: nowIso,
  };

  supplierDatabase.unshift(newSupplier);
  savePersistentData('supplier-database.json', supplierDatabase);
  syncSupplierToDatabase(newSupplier);
  syncToSupplierMaster(newSupplier);
  return newSupplier;
}

export function updateSupplier(
  id: string,
  data: Partial<Omit<SupplierRecord, 'id' | 'createdAt'>>
): SupplierRecord {
  const supplier = getSupplierById(id);
  if (!supplier) {
    throw new Error(`Supplier '${id}' not found.`);
  }

  if (data.code && data.code.toUpperCase() !== supplier.code.toUpperCase()) {
    if (supplierDatabase.some((s) => s.id !== supplier.id && s.code.toUpperCase() === data.code!.toUpperCase())) {
      throw new Error(`Supplier code '${data.code}' is already used by another supplier.`);
    }
    supplier.code = data.code.toUpperCase();
  }

  if (data.name) supplier.name = data.name.trim();
  if (data.phone) supplier.phone = data.phone.trim();
  if (data.email) supplier.email = data.email.trim();
  if (data.address) supplier.address = data.address.trim();
  if (data.paymentTerms) supplier.paymentTerms = data.paymentTerms;
  if (data.status) supplier.status = data.status;
  if (data.bankAccounts) supplier.bankAccounts = data.bankAccounts;
  supplier.updatedAt = new Date().toISOString();
  savePersistentData('supplier-database.json', supplierDatabase);
  syncSupplierToDatabase(supplier);
  syncToSupplierMaster(supplier);

  return supplier;
}

export function deleteSupplier(id: string): { success: boolean; message: string } {
  const index = supplierDatabase.findIndex((s) => s.id === id || s.code === id);
  if (index === -1) {
    throw new Error(`Supplier '${id}' not found.`);
  }

  const supplier = supplierDatabase[index];

  // Check if supplier has linked POs
  const hasLinkedPOs = poDatabase.some((p) => p.supplierId === supplier.id || p.supplierSnapshot.id === supplier.id);
  if (hasLinkedPOs) {
    // If supplier has transactions, soft-deactivate instead of hard delete to preserve historical integrity
    supplier.status = 'INACTIVE';
    supplier.updatedAt = new Date().toISOString();
    return {
      success: true,
      message: `Supplier '${supplier.name}' memiliki riwayat transaksi PO aktif, status berhasil diubah menjadi NONAKTIF.`,
    };
  }

  supplierDatabase.splice(index, 1);
  savePersistentData('supplier-database.json', supplierDatabase);
  const smIdx = SUPPLIER_MASTER.findIndex((s) => s.id === supplier.id || s.code === supplier.code);
  if (smIdx >= 0) {
    SUPPLIER_MASTER.splice(smIdx, 1);
  }
  return {
    success: true,
    message: `Supplier '${supplier.name}' berhasil dihapus.`,
  };
}

/**
 * Computes full workspace analytics for a supplier:
 * 1. Transaction history (all POs and GRs)
 * 2. Product Catalog supplied by this vendor
 * 3. Historical Price changes per item purchased from this vendor across PO transactions
 */
export function getSupplierWorkspaceData(supplierId: string): SupplierWorkspaceData {
  const supplier = getSupplierById(supplierId);
  if (!supplier) {
    throw new Error(`Supplier '${supplierId}' not found.`);
  }

  // 1. Locate all POs for this supplier
  const linkedPOs = poDatabase.filter(
    (p) => p.supplierId === supplier.id || p.supplierSnapshot?.id === supplier.id || p.supplierSnapshot?.code === supplier.code
  ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // 2. Locate all GRs for this supplier
  const linkedGRs = grDatabase.filter(
    (g) => g.supplierId === supplier.id || g.supplierName.toLowerCase() === supplier.name.toLowerCase()
  ).sort((a, b) => new Date(b.receiveDate).getTime() - new Date(a.receiveDate).getTime());

  // 3. Aggregate Product Catalog & Historical Price Records
  const productMap = new Map<string, SupplierProductPriceHistory>();

  for (const po of linkedPOs) {
    for (const item of po.items) {
      const existing = productMap.get(item.itemId) || {
        itemId: item.itemId,
        itemCode: item.itemCode,
        itemName: item.itemName,
        category: MASTER_ITEMS.find((m) => m.id === item.itemId)?.category || 'Packaging & Warehouse',
        uomCode: item.uomCode,
        lastPurchasePrice: item.unitPrice,
        lastPurchaseDate: po.date,
        minPrice: item.unitPrice,
        maxPrice: item.unitPrice,
        averagePrice: item.unitPrice,
        totalQuantityPurchased: 0,
        totalSpend: 0,
        transactionsCount: 0,
        priceHistory: [],
      };

      existing.totalQuantityPurchased += item.orderedQty;
      existing.totalSpend += item.subtotal;
      existing.transactionsCount += 1;

      if (item.unitPrice < existing.minPrice) existing.minPrice = item.unitPrice;
      if (item.unitPrice > existing.maxPrice) existing.maxPrice = item.unitPrice;

      // Update last price if PO is more recent
      if (new Date(po.date).getTime() >= new Date(existing.lastPurchaseDate).getTime()) {
        existing.lastPurchasePrice = item.unitPrice;
        existing.lastPurchaseDate = po.date;
      }

      existing.priceHistory.push({
        poId: po.id,
        poNumber: po.poNumber,
        poDate: po.date,
        orderedQty: item.orderedQty,
        unitPrice: item.unitPrice,
        subtotal: item.subtotal,
        status: po.status,
      });

      productMap.set(item.itemId, existing);
    }
  }

  // Calculate averages & sort price history
  const productCatalog = Array.from(productMap.values()).map((p) => ({
    ...p,
    averagePrice: p.totalQuantityPurchased > 0 ? Math.round(p.totalSpend / p.totalQuantityPurchased) : p.lastPurchasePrice,
    priceHistory: p.priceHistory.sort((a, b) => new Date(b.poDate).getTime() - new Date(a.poDate).getTime()),
  }));

  // Calculate overall KPIs
  const totalSpend = linkedPOs.reduce((sum, p) => sum + p.total, 0);
  const totalGoodQty = linkedGRs.reduce((sum, g) => sum + g.totalGoodQty, 0);
  const totalRejectedQty = linkedGRs.reduce((sum, g) => sum + g.totalRejectedQty, 0);
  const totalReceived = totalGoodQty + totalRejectedQty;
  const overallAcceptanceRate = totalReceived > 0 ? Number(((totalGoodQty / totalReceived) * 100).toFixed(1)) : 100;

  return {
    supplier,
    kpis: {
      totalSpend,
      totalPOsCount: linkedPOs.length,
      totalGRsCount: linkedGRs.length,
      productsSuppliedCount: productCatalog.length,
      overallAcceptanceRate,
    },
    productCatalog,
    purchaseOrders: linkedPOs,
    goodsReceipts: linkedGRs,
  };
}
